import React, { useEffect, useState } from "react";
import AdsNavbar from "../AdsNavbar";
import Loader from "../../../components/Loader";
import Calling from "../../../api/Calling";
import { toast } from "react-toastify";
import NotFound from "../../../components/ul/NotFound";
import generateCaptcha from "../../../components/captcha";

const InhouseAds = () => {
  const [loader, setLoader] = useState(false);
  const [ads, setAds] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAd, setNewAd] = useState({ a_id: "", a_type: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAds, setSelectedAds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const adTypes = ["banner", "interstitial", "native", "video", "onlyimage"];

  const fetchData = async () => {
    setLoader(true);
    try {
      const resp = await Calling.getInhouseAds();
      if (resp?.status) {
        setAds(resp?.data);
      } else {
        toast.error(resp?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredAds = ads.filter(
    (ad) =>
      ad.a_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ad.a_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (id) => {
    setEditingId(id);
  };

  const handleSave = async (id) => {
    const adToUpdate = ads.find((ad) => ad._id === id);
    setLoader(true);
    try {
      const resp = await Calling.updateInhouseAds(id, {
        a_id: adToUpdate.a_id,
        a_type: adToUpdate.a_type,
      });
      if (resp?.status) {
        toast.success(resp?.message);
        fetchData();
      } else {
        toast.error(resp?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setEditingId(null);
      setLoader(false);
    }
  };

  const handleInputChange = (id, field, value) => {
    setAds(
      ads.map((item) => (item._id === id ? { ...item, [field]: value } : item))
    );
  };

  const handleAddSubmit = async () => {
    if (newAd.a_id.trim() === "" || newAd.a_type.trim() === "") {
      toast.error("Please fill all fields");
      return;
    }

    setLoader(true);
    try {
      const body = {
        a_id: newAd.a_id.replace(/ /g, ""),
        a_type: newAd.a_type,
      };
      const resp = await Calling.addInhouseAds(body);
      if (resp?.status) {
        toast.success(resp?.message);
        setNewAd({ a_id: "", a_type: "" });
        setShowAddForm(false);
        fetchData();
      } else {
        toast.error(resp?.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoader(false);
    }
  };

  const handleBulkDelete = async (id = null) => {
    if (selectedAds.length === 0) return toast.error("No ads selected");

    const content = `Delete ${selectedAds.length} selected ads?`;
    const check = generateCaptcha(content);
    if (!check) return toast.error("Answer is not correct");

    setLoader(true);
    try {
      let body = {};
      if (id) {
        body = { id: [id] };
      } else {
        body = {
          id: selectedAds,
        };
      }
      const resp = await Calling.deleteInhouseAds(body);
      if (resp?.status) {
        toast.success(resp?.message);
        setSelectedAds([]);
        setSelectAll(false);
        fetchData();
      } else {
        toast.error(resp?.message);
      }
    } catch (error) {
      console.error(error);
      toast.error("Server Error");
    } finally {
      setLoader(false);
    }
  };

  const handleSelectAd = (id) => {
    if (selectedAds.includes(id)) {
      setSelectedAds(selectedAds.filter((item) => item !== id));
    } else {
      setSelectedAds([...selectedAds, id]);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedAds([]);
    } else {
      setSelectedAds(filteredAds.map((item) => item._id));
    }
    setSelectAll(!selectAll);
  };

  return (
    <>
      {loader ? (
        <Loader loader={loader} />
      ) : (
        <div className="w-full text-main-font mt-16 px-2 md:px-4 md:ml-[4.4rem]">
          <h1 className="text-xl sm:text-2xl font-bold mt-4 text-main-font w-auto">
            Ad Management
          </h1>

          <div className="bg-main-white rounded-xl">
            <AdsNavbar selectState={"inhouseads"} />
            <hr className="mt-2 bg-main-border border-[1px]" />

            <div className="px-4 sm:px-6 pt-4 sm:pt-6">
              <div className="flex flex-col sm:flex-row justify-between items-center mb-4 sm:mb-5">
                <h1 className="text-lg sm:text-2xl font-bold text-main-font mb-2 sm:mb-0">
                  Inhouse Ads
                </h1>

                <div className="flex gap-3 items-center">
                  {selectedAds?.length > 0 && (
                    <button
                      onClick={() => handleBulkDelete()}
                      className="px-3 py-2 text-sm sm:text-md bg-red-600 text-main-white rounded-3xl hover:bg-red-700 transition"
                    >
                      Delete Selected
                    </button>
                  )}

                  <button
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="px-3 py-2 text-sm sm:text-md text-main-white bg-green-600 rounded-3xl hover:bg-green-700 transition"
                  >
                    + Add Inhouse Ad
                  </button>
                </div>
              </div>

              <hr className="border-main-border" />

              {showAddForm && (
                <div className="mt-4 sm:mt-6 p-3 sm:p-4 rounded-lg space-y-4 bg-main-bg">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div className="flex flex-col sm:flex-row items-center">
                      <label className="w-full sm:w-1/4 text-sm sm:text-base text-main-font font-medium mb-2 sm:mb-0">
                        Ad ID:
                      </label>
                      <input
                        type="text"
                        placeholder="Enter Ad ID"
                        value={newAd.a_id}
                        onChange={(e) =>
                          setNewAd({ ...newAd, a_id: e.target.value })
                        }
                        className="w-full p-2 border border-main-border rounded text-sm"
                      />
                    </div>

                    <div className="flex flex-col sm:flex-row items-center">
                      <label className="w-full sm:w-1/4 text-sm sm:text-base text-main-font font-medium mb-2 sm:mb-0">
                        Ad Type:
                      </label>
                      <select
                        value={newAd.a_type}
                        onChange={(e) =>
                          setNewAd({ ...newAd, a_type: e.target.value })
                        }
                        className="w-full p-2 border border-main-border rounded text-sm"
                      >
                        <option value="">Select Type</option>
                        {adTypes.map((t, i) => (
                          <option key={i} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex gap-3 py-2">
                    <button
                      onClick={handleAddSubmit}
                      className="px-3 py-1 text-sm sm:text-md text-main-white bg-main-blue rounded hover:bg-main-blue/80 transition"
                    >
                      Submit
                    </button>
                    <button
                      onClick={() => setShowAddForm(false)}
                      className="px-3 py-1 text-sm sm:text-md border border-main-delete bg-main-white text-main-delete rounded hover:bg-main-delete/10 transition"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* SEARCH BAR */}
            <div className="px-4 sm:px-6 py-4 sm:py-6">
              <input
                type="text"
                placeholder="Search Ads..."
                value={searchTerm}
                onChange={handleSearch}
                className="p-2 border rounded-lg w-full text-sm"
              />
            </div>

            {/* TABLE */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className="bg-main-th text-main-font uppercase">
                  <tr>
                    <th className="text-left px-4 py-2 rounded-tl-lg">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th className="text-left px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium ">
                      No
                    </th>
                    <th className="text-left px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium">
                      Ad ID
                    </th>
                    <th className="text-left px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium">
                      Type
                    </th>
                    <th className="text-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-tr-lg">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredAds.length > 0 ? (
                    filteredAds.map((item, index) => (
                      <tr key={index} className="hover:bg-main-thHover">
                        <td className="px-4 py-2 text-sm">
                          <input
                            type="checkbox"
                            checked={selectedAds.includes(item._id)}
                            onChange={() => handleSelectAd(item._id)}
                          />
                        </td>

                        <td className="px-4 py-2 text-sm">{index + 1}</td>

                        <td className="px-4 py-2 text-sm break-all">
                          {editingId === item._id ? (
                            <input
                              type="text"
                              value={item.a_id}
                              onChange={(e) =>
                                handleInputChange(
                                  item._id,
                                  "a_id",
                                  e.target.value
                                )
                              }
                              className="p-2 w-full border rounded text-sm"
                            />
                          ) : (
                            item.a_id
                          )}
                        </td>

                        <td className="px-4 py-2 text-sm capitalize">
                          {editingId === item._id ? (
                            <select
                              value={item.a_type}
                              onChange={(e) =>
                                handleInputChange(
                                  item._id,
                                  "a_type",
                                  e.target.value
                                )
                              }
                              className="p-2 w-full border rounded text-sm"
                            >
                              {adTypes.map((t, i) => (
                                <option key={i} value={t}>
                                  {t}
                                </option>
                              ))}
                            </select>
                          ) : (
                            item.a_type
                          )}
                        </td>

                        <td className="text-center px-4 py-2">
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() =>
                                editingId === item._id
                                  ? handleSave(item._id)
                                  : handleEdit(item._id)
                              }
                              className={`px-4 py-1.5 text-sm text-main-white rounded transition-colors ${
                                editingId === item._id
                                  ? "bg-green-500 hover:bg-green-600"
                                  : "bg-blue-500 hover:bg-blue-600"
                              }`}
                            >
                              {editingId === item._id ? "Save" : "Edit"}
                            </button>

                            <button
                              onClick={() => handleBulkDelete(item._id)}
                              className="px-4 py-1.5 text-sm bg-red-500 hover:bg-red-600 text-main-white rounded"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={11}>
                        <NotFound />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InhouseAds;
